<!-- Loading and error -->
<app-loading-status *ngIf="((loaderService.isLoading | async) && !selectedTier); else notLoading"></app-loading-status>
<app-alert-status *ngIf="loadingError" [loadingError]="loadingError"></app-alert-status>
<app-alert-status *ngIf="alert" [alert]="alert" (emitAlertCancelled)="alert = $event"></app-alert-status>

<ng-template #notLoading>

    <!-- Table -->
    <app-dynamic-table 
        *ngIf="currentProject.tiers" 
        [data]="currentProject.tiers" 
        [columns]="tierTableColumns"
        [currentYear]="currentYear"
        [tableTitle]="'tierList'"
        [primaryKey]="'id'"
        [onRowClick]="onTierClick">
    </app-dynamic-table>
    
</ng-template>

<!-- Selected Tier -->
<app-slider-panel *ngIf="selectedTier" [onClose]="closeTierSlider">

    <!-- Heading -->
    <app-area-heading [heading]="tierLayout.heading" [subheading]="tierLayout.subheading" [backRoute]="tierLayout.backRoute"></app-area-heading>

    <!-- Tier Details -->
    <ng-container *ngIf="{ darkMode: darkMode$ | async } as ui">
        <hr>
        <h4 class="my-4" [ngClass]="{'text-primary-light': ui.darkMode, 'text-primary': !ui.darkMode}">
            Tier Details
            <i class="far fa-info-circle form-info-icon" ngbTooltip="Changes made here affect all versions of this tier, in all years." placement="right" container="body" triggers="hover focus click" tabindex="0"></i>
        </h4>
        <p class="mb-3" [ngClass]="{'text-light': ui.darkMode, 'text-muted': !ui.darkMode}">
            This section edits the details of the tier (left-hand side of the outputs).
        </p>
    </ng-container>
    <app-dynamic-form #tierDetailsFormRef [data]="selectedTier" [blocks]="tierDetailsForm" (dataUpdate)="onTierDetailsSave($event)"></app-dynamic-form>

    <!-- Tier Years -->
    <ng-container *ngIf="{ darkMode: darkMode$ | async } as ui">
        <hr>
        <h4 class="my-4" [ngClass]="{'text-primary-light': ui.darkMode, 'text-primary': !ui.darkMode}">
            Tier Years
            <i class="far fa-info-circle form-info-icon" ngbTooltip="Changes made in this section affect this tier only." placement="right" container="body" triggers="hover focus click" tabindex="0"></i>
        </h4>
        <div *ngIf="isTierReportYearsLoading" class="mb-3" [ngClass]="{'text-light': ui.darkMode, 'text-muted': !ui.darkMode}">
            <i class="fas fa-spinner fa-spin"></i> Loading...
        </div>
        <div class="table-responsive mb-3">
            <table class="table table-sm table-striped mb-0" [ngClass]="{'table-dark': ui.darkMode}">
                <thead>
                    <tr>
                        <th style="width: 30%">Report ID</th>
                        <th style="width: 30%">Report Year</th>
                        <th style="width: 25%">Is Visible</th>
                        <th style="width: 15%">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr *ngFor="let row of tierReportYears; let i = index" [ngClass]="{'table-dark': ui.darkMode}">
                        <td>
                            <input *ngIf="row.isNew" type="number" class="form-control form-control-sm" [(ngModel)]="row.reportId" placeholder="Report ID">
                            <span *ngIf="!row.isNew">{{ row.reportId }}</span>
                        </td>
                        <td>
                            <input *ngIf="row.isNew" type="number" class="form-control form-control-sm" [(ngModel)]="row.reportYear" placeholder="Report Year">
                            <span *ngIf="!row.isNew">{{ row.reportYear }}</span>
                        </td>
                        <td>
                            <select *ngIf="row.isNew" class="form-control form-control-sm" [(ngModel)]="row.isVisible">
                                <option *ngFor="let opt of Constants?.yesNo" [ngValue]="opt.id">{{ opt.name }}</option>
                            </select>
                            <span *ngIf="!row.isNew">
                                <i class="fas" [ngClass]="row.isVisible === 'Y' ? 'fa-check text-success' : 'fa-times text-danger'"></i>
                            </span>
                        </td>
                        <td>
                            <button *ngIf="row.isNew" class="btn btn-primary btn-sm mr-1" (click)="saveTierYear(row, i)" title="Save">
                                <i class="fas fa-save"></i>
                            </button>
                            <button class="btn btn-outline-secondary btn-sm" (click)="deleteTierYear(row, i)" title="Delete">
                                <i class="fas fa-trash"></i>
                            </button>
                        </td>
                    </tr>
                    <tr *ngIf="!isTierReportYearsLoading && (!tierReportYears || tierReportYears.length === 0)">
                        <td colspan="4" class="text-center">No Tier Years defined</td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div class="mb-4">
            <button class="btn btn-secondary btn-sm" (click)="addTierYear()" [disabled]="isTierReportYearsLoading">
                <i class="fas fa-plus"></i> Add Tier Year
            </button>
        </div>
    </ng-container>

    <ng-container *ngIf="selectedTier.reportDetails">
        
        <!-- Basic Report Details -->
        <ng-container *ngIf="{ darkMode: darkMode$ | async } as ui">
            <hr>
            <h4 class="my-4" [ngClass]="{'text-primary-light': ui.darkMode, 'text-primary': !ui.darkMode}">
                Report Details
                <i class="far fa-info-circle form-info-icon" ngbTooltip="Changes made here affect all versions of this chart, in all years." placement="right" container="body" triggers="hover focus click" tabindex="0"></i>
            </h4>
            <p class="mb-3" [ngClass]="{'text-light': ui.darkMode, 'text-muted': !ui.darkMode}">
                This section edits the basic details of the report linked to the tier above.
            </p>
        </ng-container>
        <app-dynamic-form #reportDetailsFormRef [data]="selectedTier.reportDetails" [blocks]="reportDetailsForm" (id)="idReceived($event)" (dataUpdate)="onReportDetailsSave($event)"></app-dynamic-form>

        <hr>

        <ng-container *ngIf="{ darkMode: darkMode$ | async } as ui">
            <h4 class="my-4" [ngClass]="{'text-primary-light': ui.darkMode, 'text-primary': !ui.darkMode}">
                Chart Stored Procedure
                <i class="far fa-info-circle form-info-icon" ngbTooltip="Changes made in this section affect all versions of this chart, in all years." placement="right" container="body" triggers="hover focus click" tabindex="0"></i>
            </h4>
            <p class="mb-3 font-weight-bold" [ngClass]="{'text-light': ui.darkMode, 'text-dark': !ui.darkMode}">
                <span class="badge badge-warning text-dark mr-2 px-2 py-1">
                    <i class="fas fa-exclamation-triangle"></i>
                </span>
                Use with caution - changing Stored Procedure changes the calculation and may break the report.
            </p>
        </ng-container>

        <div *ngIf="{ darkMode: darkMode$ | async } as ui" class="border rounded mb-2 mt-5 p-3"
            [ngClass]="{'border-dark-700': ui.darkMode}">
            <div class="mb-3">
                <label class="font-weight-bold mb-1 d-block"
                    [ngClass]="{'text-light': ui.darkMode}">Generic Stored Procedure</label>
                <select class="form-control w-25"
                    [ngClass]="{'text-light bg-dark-900 border-dark-700': ui.darkMode}"
                    [disabled]="true"
                    [(ngModel)]="storedProcedures.genericSp">
                    <option *ngFor="let option of Constants.yesNo" [ngValue]="option.id">{{ option.name }}</option>
                </select>
            </div>
            <app-project-toolkit-stored-procedures-select
                [storedProcedures]="storedProcedures"
                [isLoading]="isStoredProceduresLoading"
                [disabled]="storedProcedures.genericSp !== 'Y'"
                [filterPrefix]="storedProcedures.genericSp === 'Y' ? 'sp_a_generic_' : ''"
                (emitSelectedItems)="updateStoredProcedure($event.objectName, $event.objectId)">
            </app-project-toolkit-stored-procedures-select>
            <p *ngIf="storedProcedures.genericSp !== 'Y'" class="text-muted mt-2 mb-0">
                <i class="fas fa-info-circle mr-1"></i>Unable to change Stored Procedure, due to this report not using a Generic Stored Procedure; please contact the Development team for further support.
            </p>
            <div class="d-flex justify-content-start mt-3">
                <button class="btn btn-primary btn-sm" (click)="saveMetadata()"
                    [disabled]="!hasUnsavedMetadataChanges || isSavingMetadata">
                    <i class="fas fa-save mr-1"></i>
                    {{ isSavingMetadata ? 'Saving...' : 'Save Stored Procedure' }}
                </button>
            </div>
        </div>

        <ng-container *ngIf="{ darkMode: darkMode$ | async } as ui">
            <hr>
            <h4 class="my-4" [ngClass]="{'text-primary-light': ui.darkMode, 'text-primary': !ui.darkMode}">
                View Types
                <i class="far fa-info-circle form-info-icon" ngbTooltip="Changes made in this section affect all versions of this chart, in all years." placement="right" container="body" triggers="hover focus click" tabindex="0"></i>
            </h4>
            <p class="mb-3" [ngClass]="{'text-light': ui.darkMode, 'text-muted': !ui.darkMode}">The first view in the order will be the default.</p>
        </ng-container>

        <!-- View Types Actions -->
        <ng-container *ngIf="selectedTier.reportDetails.reportTypes && { darkMode: darkMode$ | async } as ui">
            <div class="table-responsive mb-4">
                <table class="table table-sm table-striped mb-0" [ngClass]="{'table-dark': ui.darkMode}">
                    <thead>
                        <tr>
                            <th style="width: 40%">Chart Type</th>
                            <th style="width: 30%">View Name</th>
                            <th style="width: 15%">Order</th>
                            <th style="width: 15%">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr *ngFor="let reportType of selectedTier.reportDetails.reportTypes; let i = index"
                            [ngClass]="{'table-dark': ui.darkMode}"
                            [attr.data-charttype]="reportType.chartType || reportType.chartTypeDupe">
                            <td>
                                <select class="form-control form-control-sm"
                                    [(ngModel)]="reportType.chartTypeDupe"
                                    (ngModelChange)="onReportTypeFieldChange(reportType, i)">
                                    <option value="">-- Select Chart Type --</option>
                                    <option *ngFor="let chartType of Constants?.chartTypes"
                                        [value]="chartType.id">
                                        [{{ chartType.id }}] {{ chartType.name }}
                                    </option>
                                </select>
                            </td>
                            <td>
                                <input type="text" class="form-control form-control-sm"
                                    [(ngModel)]="reportType.viewName"
                                    (ngModelChange)="onReportTypeFieldChange(reportType, i)"
                                    placeholder="Enter view name">
                            </td>
                            <td>
                                <select class="form-control form-control-sm"
                                    [ngModel]="reportType.displaySequence"
                                    (ngModelChange)="onDisplaySequenceChange(i, $event)">
                                    <option *ngFor="let order of getDisplaySequenceOptions()"
                                        [ngValue]="order">
                                        {{ order }}
                                    </option>
                                </select>
                            </td>
                            <td>
                                <button class="btn btn-outline-secondary btn-sm"
                                    (click)="deleteReportType(reportType, i)"
                                    title="Delete">
                                    <i class="fas fa-trash"></i>
                                </button>
                            </td>
                        </tr>
                        <tr *ngIf="!selectedTier.reportDetails.reportTypes || selectedTier.reportDetails.reportTypes.length === 0">
                            <td colspan="4" class="text-center">
                                No View Types defined
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="d-flex align-items-center mb-4">
                <button class="btn btn-secondary btn-sm mr-2" (click)="addReportType()">
                    <i class="fas fa-plus"></i> Add View Type
                </button>
                <button class="btn btn-primary btn-sm" (click)="saveAllReportTypes()" [disabled]="!hasUnsavedViewTypesChanges">
                    <i class="fas fa-save"></i> Save View Types
                </button>
            </div>
        </ng-container>

        <ng-container *ngIf="{ darkMode: darkMode$ | async } as ui">
            <hr>
            <h4 class="my-4" [ngClass]="{'text-primary-light': ui.darkMode, 'text-primary': !ui.darkMode}">Report Views</h4>
            <div class="mb-4">
                <button class="btn btn-outline-primary btn-sm" (click)="openReportViewDetailsSlider()">
                    <i class="fas fa-table mr-1"></i> Open Report View Details
                </button>
            </div>
        </ng-container>

        <ng-container *ngIf="{ darkMode: darkMode$ | async } as ui">
            <hr>
            <h4 class="my-4" [ngClass]="{'text-primary-light': ui.darkMode, 'text-primary': !ui.darkMode}">
                Report Options
                <i class="far fa-info-circle form-info-icon" ngbTooltip="Changes made in this section are year specific and only apply to the currently selected year." placement="right" container="body" triggers="hover focus click" tabindex="0"></i>
            </h4>
            <p class="mb-3" [ngClass]="{'text-light': ui.darkMode, 'text-muted': !ui.darkMode}">This section defines the data used to create the report.</p>
        </ng-container>

        <!-- Report Options/parameters -->
        <app-dynamic-table
            *ngIf="selectedTier.reportDetails.options"
            [data]="selectedTier.reportDetails.options"
            [columns]="reportOptionsTableColumns"
            [currentYear]="currentYear"
            [tableTitle]="'reportOptions'"
            [primaryKey]="'optionId'"
            [onRowClick]="onOptionClick"
            [hideExportButton]="true"
            [hideSearch]="true"
            >

        </app-dynamic-table>

        <div class="mb-4">
            <button class="btn btn-secondary btn-sm" (click)="openNewOption()">
                <i class="fas fa-plus"></i> Add Option
            </button>
        </div>

    </ng-container>
    
</app-slider-panel>

<!-- Report View Details Slider -->
<app-slider-panel *ngIf="isReportViewSliderOpen" [layer]="1" [onClose]="closeReportViewSlider">

    <!-- Heading -->
    <app-area-heading [heading]="reportViewLayout.heading" [subheading]="reportViewLayout.subheading" [backRoute]="reportViewLayout.backRoute"></app-area-heading>

    <app-loading-status *ngIf="isReportViewDetailsLoading"></app-loading-status>

    <app-dynamic-table *ngIf="!isReportViewDetailsLoading && reportViewDetails && reportViewDetails.length > 0"
        [data]="reportViewDetails"
        [columns]="reportviewDetailsTableColumns"
        [tableTitle]="'reportViewDetails'"
        [primaryKey]="'optionId'"
        [disableRowClick]="true">
    </app-dynamic-table>

    <p *ngIf="!isReportViewDetailsLoading && (!reportViewDetails || reportViewDetails.length === 0)" class="text-muted mt-3">
        No Report View Details found.
    </p>

</app-slider-panel>

<!-- Selected Option -->
<app-slider-panel *ngIf="selectedOption" [layer]="1" [onClose]="closeOptionSlider">

    <!-- Heading -->
    <app-area-heading [heading]="optionLayout.heading" [subheading]="optionLayout.subheading" [backRoute]="optionLayout.backRoute"></app-area-heading>

    <!-- Series and parameters -->
    <app-dynamic-form
        #optionDetailsFormRef
        [data]="selectedOption"
        [blocks]="selectedOption.isNew ? newOptionDetailsForm : optionDetailsForm"
        (dataUpdate)="onOptionSave($event)"></app-dynamic-form>
    <div *ngIf="!selectedOption.isNew && selectedOption.optionId !== 0" class="d-flex justify-content-end mb-3">
        <button class="btn btn-outline-danger btn-sm" (click)="onOptionDelete()">
            <i class="fas fa-trash mr-1"></i> Delete Option
        </button>
    </div>
    <ng-container *ngIf="!selectedOption.isNew">
        <!-- Parameter year selector -->
        <div *ngIf="combinedParameterYears.length" class="d-flex align-items-center mt-3 mb-3">
            <label class="font-weight-bold mr-2 mb-0 text-secondary">Parameter Year:</label>
            <select class="form-control form-control-sm w-auto"
                [ngModel]="selectedTier.reportDetails.activeParameterYear"
                [compareWith]="compareYears"
                (ngModelChange)="onParameterYearChange(+$event)">
                <option *ngFor="let year of combinedParameterYears" [ngValue]="year">{{ year }}</option>
            </select>
        </div>

        <ng-container *ngFor="let series of selectedOption.series">
            <ng-container *ngIf="{ darkMode: darkMode$ | async } as ui">

            <hr class="mt-3">
            <h5 class="text-secondary my-3">Series {{ series.seriesName }}</h5>
            <app-dynamic-form [data]="series" [blocks]="seriesDetailsForm"></app-dynamic-form>
            <label class="font-weight-bold mb-3" [ngClass]="{'text-secondary': ui.darkMode, 'text-secondary-light': !ui.darkMode}">Report Parameters</label>
            <app-dynamic-table 
                *ngIf="series.parameters" 
                [data]="series.parameters" 
                [columns]="parametersTableColumns" 
                [currentYear]="currentYear"
                [tableTitle]="'parameters'"
                [primaryKey]="'parameterId'"
                [onRowClick]="onParameterClick"
                [hideExportButton]="true"
                [hideSearch]="true"> 
            </app-dynamic-table>
            <div class="mb-4">
                <button class="btn btn-secondary btn-sm" (click)="openNewParameter(series.seriesId)">
                    <i class="fas fa-plus"></i> Add Parameter
                </button>
            </div>
        </ng-container>
        </ng-container>
        <ng-container *ngIf="!selectedOption.series || selectedOption.series.length === 0">
            <hr class="mt-3">
            <label class="font-weight-bold mb-3 text-secondary">Report Parameters</label>
            <p class="text-muted">No parameters defined yet.</p>
            <div class="mb-4">
                <button class="btn btn-secondary btn-sm" (click)="openNewParameter()">
                    <i class="fas fa-plus"></i> Add Parameter
                </button>
            </div>
        </ng-container>
    </ng-container>
    <ng-container *ngIf="selectedOption.isNew">
        <hr class="mt-3">
        <p class="text-muted">Save the option first to manage its parameters.</p>
    </ng-container>

</app-slider-panel>

<!-- Selected Parameter -->
<app-slider-panel *ngIf="selectedParameter" [layer]="2" [onClose]="closeParameterSlider">

    <!-- Heading -->
    <app-area-heading [heading]="parameterLayout.heading" [subheading]="parameterLayout.subheading" [backRoute]="parameterLayout.backRoute"></app-area-heading>
    <hr>

    <!-- Series selector -->
    <ng-container *ngIf="{ darkMode: darkMode$ | async } as ui">
        <div class="border rounded mb-3 p-3" [ngClass]="{'border-dark-700': ui.darkMode}">
            <div class="d-flex justify-content-between align-items-center">
                <div>
                    <label class="font-weight-bold mb-1 d-block" [ngClass]="{'text-light': ui.darkMode}">Series</label>
                    <span [ngClass]="{'text-light': ui.darkMode}">
                        <strong>ID:</strong> {{ selectedParameter.seriesId !== null && selectedParameter.seriesId !== undefined ? selectedParameter.seriesId : '-' }}
                        <span class="ml-2"><strong>Name:</strong> {{ selectedParameter.seriesName || '-' }}</span>
                    </span>
                </div>
                <button class="btn btn-outline-primary btn-sm" (click)="toggleSeriesPicker()">
                    <i class="fas" [ngClass]="isSeriesPickerOpen ? 'fa-times' : 'fa-search'"></i>
                    {{ isSeriesPickerOpen ? 'Close' : 'Change / Create Series' }}
                </button>
            </div>

            <div *ngIf="isSeriesPickerOpen" class="mt-3">
                <div class="input-group input-group-sm mb-2">
                    <input type="text" class="form-control"
                        placeholder="Search by series ID or name"
                        [ngModel]="seriesSearchText"
                        (ngModelChange)="onSeriesSearchChange($event)"
                        (keyup.enter)="applySeriesSearchNow()"
                        [ngClass]="{'text-light bg-dark-900 border-dark-700': ui.darkMode}">
                    <div class="input-group-append">
                        <button class="btn btn-outline-primary" type="button" (click)="applySeriesSearchNow()">
                            <i class="fas fa-search"></i> Search
                        </button>
                    </div>
                </div>
                <div *ngIf="isLoadingReportSeries" class="text-muted small">Loading series...</div>
                <div class="table-responsive" style="max-height: 240px; overflow-y: auto;" *ngIf="!isLoadingReportSeries">
                    <table class="table table-sm table-striped mb-0" [ngClass]="{'table-dark': ui.darkMode}">
                        <thead>
                            <tr>
                                <th style="width: 20%">ID</th>
                                <th style="width: 60%">Name</th>
                                <th style="width: 20%"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr *ngFor="let s of filteredReportSeries">
                                <td>{{ s.seriesId }}</td>
                                <td>{{ s.seriesName }}</td>
                                <td>
                                    <button class="btn btn-outline-primary btn-sm" (click)="selectSeriesForParameter(s)">Select</button>
                                </td>
                            </tr>
                            <tr *ngIf="!appliedSeriesSearch">
                                <td colspan="3" class="text-center text-muted">Enter a search term to find series</td>
                            </tr>
                            <tr *ngIf="appliedSeriesSearch && (!filteredReportSeries || filteredReportSeries.length === 0)">
                                <td colspan="3" class="text-center text-muted">No matching series</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <hr>
                <label class="font-weight-bold mb-1 d-block" [ngClass]="{'text-light': ui.darkMode}">Create New Series</label>
                <div class="row">
                    <div class="col-md-6 mb-2">
                        <input type="text" class="form-control form-control-sm" placeholder="Series name"
                            [(ngModel)]="newSeriesName"
                            [ngClass]="{'text-light bg-dark-900 border-dark-700': ui.darkMode}">
                    </div>
                    <div class="col-md-3 mb-2">
                        <input type="number" class="form-control form-control-sm" placeholder="Display sequence"
                            [(ngModel)]="newSeriesDisplaySequence"
                            [ngClass]="{'text-light bg-dark-900 border-dark-700': ui.darkMode}">
                    </div>
                    <div class="col-md-3 mb-2">
                        <button class="btn btn-primary btn-sm" (click)="createNewSeries()" [disabled]="isCreatingSeries">
                            <i class="fas fa-plus"></i> {{ isCreatingSeries ? 'Creating...' : 'Create' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </ng-container>

    <!-- Parameter Row 1: Type / Year / Mandatory -->
    <app-dynamic-form #parameterDetailsTopFormRef [data]="selectedParameter" [blocks]="parameterDetailsTopForm"></app-dynamic-form>

    <!-- Question Selector (between row 1 and row 2; provides Question ID & Part) -->
    <ng-container *ngIf="{ darkMode: darkMode$ | async } as ui">
        <div class="mb-3">
            <button class="btn btn-outline-primary btn-sm"
                    (click)="onQuestionsSectionToggle()"
                    *ngIf="!isQuestionSelectorOpened">
                <i class="fas fa-search"></i> Open Question Selector
            </button>
            <button class="btn btn-outline-primary btn-sm"
                    (click)="onQuestionsSectionToggle()"
                    *ngIf="isQuestionSelectorOpened">
                <i class="fas fa-times"></i> Close Question Selector
            </button>
            <div class="mt-3 pb-3 d-flex flex-column border border-dark-700 rounded"
                [ngClass]="{'text-light': ui.darkMode}" *ngIf="currentYearQuestions && isQuestionSelectorOpened">
                <!-- Search bar -->
                <div class="d-flex container">
                    <div class="input-group ml-3 mr-4 my-4">
                        <input
                            type="text"
                            class="form-control"
                            [ngModel]="searchValue"
                            (ngModelChange)="searchInput($event)"
                            (keyup.enter)="applyQuestionSearchNow()"
                            placeholder="{{ searchPlaceholder }}">
                        <div class="input-group-append">
                            <button class="btn btn-outline-primary" type="button" (click)="applyQuestionSearchNow()">
                                <i class="fas fa-search"></i> Search
                            </button>
                            <button *ngIf="searchValue.length > 0" class="btn btn-outline-secondary" type="button" (click)="clearInput()">
                                <i class="far fa-times"></i>
                            </button>
                        </div>
                    </div>
                </div>
                <div class="px-4 small text-muted" *ngIf="respondedQuestions">
                    Loaded {{ respondedQuestions.length }} questions
                    <span *ngIf="appliedSearchValue"> &middot; filtered: {{ (respondedQuestions | filterBy: ['questionText', 'questionId']: appliedSearchValue).length }}</span>
                </div>
                <!-- Scrollable results box -->
                <div style="height: 400px; overflow-y: auto;" class="border-top mt-2">
                    <!-- Prompt before search -->
                    <ng-container *ngIf="!appliedSearchValue">
                        <div class="d-flex flex-column m-2 px-4 pt-3">
                            <p class="text-muted align-self-center">Enter a search term above to find questions</p>
                        </div>
                    </ng-container>
                    <!-- Results -->
                    <ng-container *ngIf="appliedSearchValue">
                        <div *ngFor="let question of respondedQuestions | filterBy: ['questionText', 'questionId']: appliedSearchValue">
                            <!-- Nested Questions -->
                            <div class="d-flex flex-column my-2" *ngIf="isQuestionNested(question.questionType)">
                                <div class="d-flex mx-4 mt-2 mb-1 px-3 rounded">
                                    <p class="mr-3 align-self-center mb-0" [innerHTML]="question.questionText + '<br />' + ' ( ID: ' + question.questionId + ' )' | highlight: appliedSearchValue"></p>
                                    <div class="ml-2 text-dark" (click)="getQuestionDetails(question.questionId,$event)" type="button">
                                    <i class="fa fa-chevron-down"  [ngClass]="{'fa-chevron-down-DarkMode': ui.darkMode}"></i>
                                    </div>
                                </div>
                                <div class="hidden border border-dark-700 rounded ml-5 mr-4 mb-3 mt-1 pr-2" id="{{question.questionId}}">
                                    <div *ngIf="question.questionDetails.length > 0" >
                                        <ul *ngFor="let childQuestion of question.questionDetails">
                                            <li class="d-flex justify-content-between ml-4 mr-2 my-3 py-1 px-2 hover rounded" [ngClass]="{'hoverDarkMode': ui.darkMode}">
                                                <p class="align-self-center mb-0"
                                                [innerHTML]="childQuestion.questionText + '<br />' + ' ( ID: ' + childQuestion.questionId + ' )'">
                                                </p>
                                                <button
                                                class="btn btn-secondary btn-sm ml-5 align-self-start mt-1" [ngClass]="{'btn btn-light': ui.darkMode}"
                                                (click)="onSelectQuestion(childQuestion.questionId,childQuestion.questionPart, childQuestion.questionText)"
                                                >Select
                                                </button>
                                            </li>
                                        </ul>
                                    </div>
                                    <div *ngIf="question.questionDetails.length === 0" class="container mt-3">
                                        <p class="mx-5"> There is nothing to display.</p>
                                        <p class="mx-5"> Please report the issue using the Dev Hub Request feature</p>
                                    </div>
                                </div>
                            </div>
                            <!-- Flat Questions -->
                            <div class="d-flex justify-content-between mx-4 my-2 px-3 py-1 hover rounded"
                                [ngClass]="{'hoverDarkMode': ui.darkMode}" *ngIf="!isQuestionNested(question.questionType)">
                                <p class="mr-5 mb-0 align-self-center"
                                [innerHTML]="question.questionText + '<br />' + ' ( ID: ' + question.questionId + ' )' | highlight: appliedSearchValue" >
                                </p>
                                <button class="btn btn-secondary btn-sm align-self-start mt-1 mr-2 ml-5" [ngClass]="{'btn btn-light': ui.darkMode}"
                                (click)="onSelectQuestion(question.questionId,question.questionPart, question.questionText)"
                                >Select
                                </button>
                            </div>
                        </div>
                        <!-- No results -->
                        <ng-container *ngIf="(respondedQuestions | filterBy: ['questionText', 'questionId']: appliedSearchValue).length == 0">
                            <div class="d-flex flex-column m-2">
                                <p class="align-self-center">There are no results for this search</p>
                            </div>
                        </ng-container>
                    </ng-container>
                </div>
            </div>
        </div>
    </ng-container>

    <!-- Parameter Rows 2-4: Question ID/Part, Constant Value, Parameter Definition + Save -->
    <app-dynamic-form #parameterDetailsBottomFormRef [data]="selectedParameter" [blocks]="parameterDetailsBottomForm" (dataUpdate)="onParameterSave($event)"></app-dynamic-form>
    <div *ngIf="!selectedParameter.isNew" class="d-flex justify-content-end mb-3">
        <button class="btn btn-outline-danger btn-sm" (click)="onParameterDelete()">
            <i class="fas fa-trash mr-1"></i> Delete Parameter
        </button>
    </div>

    <!-- Question Metadata -->
    <hr>
    <div *ngIf="selectedParameter.questionId && { darkMode: darkMode$ | async } as ui" [ngClass]="{'text-light': ui.darkMode}">
        <strong>Question ID-Part:</strong> {{ selectedParameter.questionId }}-{{ selectedParameter.questionPart }}<br>
        <strong>Question Text:</strong> {{ selectedParameter.questionText }}
        <span *ngIf="selectedParameter.subQuestionText">| {{ selectedParameter.subQuestionText }}</span>
        <span *ngIf="selectedParameter.subQuestionHeadingText">| {{ selectedParameter.subQuestionHeadingText }}</span>
        <i *ngIf="selectedParameter.dataDefinition || selectedParameter.parentQuestionText">
            <br>Context: {{ selectedParameter.dataDefinition ? selectedParameter.dataDefinition : selectedParameter.parentQuestionText }}
        </i>
    </div>


</app-slider-panel>